/**
 * Combined tile archive (`.tile`): one file per (tx, tz) carrying a sequence of
 * typed binary payloads. Layout (little-endian):
 *
 *   bytes 0..3   — magic "TILE"
 *   bytes 4..    — chunks until end of buffer
 *
 * Each chunk:
 *   u16 type     — chunk type code (see CHUNK_TYPE_*)
 *   u32 size     — payload size in bytes (excludes the 6-byte chunk header)
 *   <size> bytes payload
 *
 * Unknown chunk types are skipped silently so future additions (lights, effects,
 * gates, …) can be added without breaking existing readers. Payloads are stored
 * raw (not pre-gzipped) so the outer `gzip_static` pass nginx runs at request
 * time can compress them all together. See the backend
 * `TiledNavigationMeshBuilder.writeTileArchive` companion for the writer.
 */

import { inflateGzip } from '../util/gzip';

const TILE_ARCHIVE_MAGIC = 0x454c4954; // "TILE" little-endian

export const CHUNK_TYPE_NAVMESH = 0x0001;
// 0x0002 was VOLGRID — retired. Backend builds may still emit it during the
// phase-out; the chunk is skipped as an unknown type by the loop below.
export const CHUNK_TYPE_OBJECTS = 0x0003;
export const CHUNK_TYPE_LIGHTS = 0x0004;
export const CHUNK_TYPE_EFFECTS = 0x0005;

export type TileArchive = {
  navmesh: Uint8Array;
  objects: Uint8Array | null;
  lights: Uint8Array | null;
  effects: Uint8Array | null;
};

type ResourceLoader = {
  loadResource(path: string): Promise<Uint8Array>;
};

const MAGIC_LENGTH = 4;
const CHUNK_HEADER_LENGTH = 6;

/** Slice an in-memory `.tile` archive blob into its typed chunk payloads. */
export function parseTileArchive(data: Uint8Array): TileArchive {
  if (data.byteLength < MAGIC_LENGTH) {
    throw new Error(`Tile archive too small: ${data.byteLength} < ${MAGIC_LENGTH} bytes`);
  }
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  if (view.getUint32(0, true) !== TILE_ARCHIVE_MAGIC) {
    throw new Error('Tile archive magic mismatch (expected "TILE")');
  }

  let navmesh: Uint8Array | null = null;
  let objects: Uint8Array | null = null;
  let lights: Uint8Array | null = null;
  let effects: Uint8Array | null = null;

  let cursor = MAGIC_LENGTH;
  while (cursor + CHUNK_HEADER_LENGTH <= data.byteLength) {
    const type = view.getUint16(cursor, true);
    const size = view.getUint32(cursor + 2, true);
    const payloadStart = cursor + CHUNK_HEADER_LENGTH;
    const payloadEnd = payloadStart + size;
    if (payloadEnd > data.byteLength) {
      throw new Error(
        `Tile archive chunk type=${type} extends past end (${payloadEnd} > ${data.byteLength})`,
      );
    }
    const payload = data.subarray(payloadStart, payloadEnd);
    switch (type) {
      case CHUNK_TYPE_NAVMESH:
        navmesh = payload;
        break;
      case CHUNK_TYPE_OBJECTS:
        objects = payload;
        break;
      case CHUNK_TYPE_LIGHTS:
        lights = payload;
        break;
      case CHUNK_TYPE_EFFECTS:
        effects = payload;
        break;
      // Unknown chunk types are skipped intentionally.
    }
    cursor = payloadEnd;
  }

  if (!navmesh) {
    throw new Error('Tile archive missing required NAVMESH chunk');
  }
  return { navmesh, objects, lights, effects };
}

/**
 * Fetch and slice one tile archive. Resolves to `null` when the network request
 * fails so callers can decide whether the missing tile is a real error or just
 * out-of-bounds streaming.
 */
export async function fetchTileArchive(
  loader: ResourceLoader,
  pathPrefix: string,
  tx: number,
  tz: number,
): Promise<TileArchive | null> {
  const path = `${pathPrefix}_tile_${tx}_${tz}.tile.gz`;
  try {
    const compressed = await loader.loadResource(path);
    const data = await inflateGzip(compressed);
    return parseTileArchive(data);
  } catch (e) {
    console.warn(`Failed to load tile archive (${tx},${tz}):`, e);
    return null;
  }
}

/**
 * In-memory cache shared between the navmesh and object streamers so each
 * `.tile` archive is fetched + parsed exactly once even though several
 * streamers consume different chunks of it. Cache entries are keyed by
 * `tx,tz` string and stored as in-flight promises so concurrent requests
 * dedupe.
 */
export class TileArchiveCache {
  private inflight = new Map<string, Promise<TileArchive | null>>();

  constructor(
    private readonly loader: ResourceLoader,
    private readonly pathPrefix: string,
  ) {}

  get(tx: number, tz: number): Promise<TileArchive | null> {
    const key = `${tx},${tz}`;
    let promise = this.inflight.get(key);
    if (!promise) {
      promise = fetchTileArchive(this.loader, this.pathPrefix, tx, tz);
      this.inflight.set(key, promise);
    }
    return promise;
  }

  /** Drop a cache entry once its tile leaves the active ring. */
  evict(tx: number, tz: number): void {
    this.inflight.delete(`${tx},${tz}`);
  }
}
